import React, { useState, useEffect, useRef } from "react";
import StatCard from "../Common/StatCard";
import UsersIcon from "../../assets/icons/users";
import LogIcon from "../../assets/icons/log";
import LeaveIcon from "../../assets/icons/leave";
import DollarIcon from "../../assets/icons/dollar";
import WorkTimerCard from "../Common/WorkTimerCard";
import Calendar from "../Common/Calendar";
import AttendanceTable from "../Common/AttendanceTable";
import useApiLoader from "../../Utils/useApiLoader";
import Loader from "../Loader/Loader";
import api from "../../setupMiddleware";
import moment from "moment";
import { ToastContainer, toast, Bounce } from "react-toastify";
import Popup from "../Common/Popup";
import PlaceholderImage from "../../assets/images/placeholder.jpg";
import { getUser } from "../../Utils/auth";
import { getAttendanceStatus } from "../../Utils/getUserUtitls";

const Attendance = () => {
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [time, setTime] = useState(0); // seconds
  const [intervalId, setIntervalId] = useState(null);
  const [stream, setStream] = useState(null);
  const [attendanceLog, setAttendanceLog] = useState([]);
  const [checkInData, setCheckInData] = useState(null);
  const [holidayData, setHolidayData] = useState([]);
  const [openImage, setOpenImage] = useState(false);
  const [openCamera, setOpenCamera] = useState(false);
  const [cameraAction, setCameraAction] = useState("Check In");
  const [selectedPhoto, setSelectedPhoto] = useState({});
  const [cameraLoading, setCameraLoading] = useState(true);
  const [cameraError, setCameraError] = useState("");
  const [clockInTime, setClockInTime] = useState(null);

  const intervalRef = useRef(null);

  const loading = useApiLoader();
  const [address, setAddress] = useState(null);

  const userData = getUser();

  const getCurrentLocation = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          try {
            // Call OpenStreetMap reverse geocoding
            const response = await api.get(
              `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`
            );
            const data = await response.data;
            const addressObj = data.address || "";

            setAddress(addressObj);
          } catch (err) {
            console.log("Failed to fetch city/state");
          }
        },
        (err) => {
          console.log(err.message);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    }
  };

  // Format time hh:mm:ss
  const formatTime = (seconds) => {
    const hrs = String(Math.floor(seconds / 3600)).padStart(2, "0");
    const mins = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
    const secs = String(seconds % 60).padStart(2, "0");
    return `${hrs}:${mins}:${secs}`;
  };

  const videoRef = useRef(null);

  const requestCameraAccess = async () => {
    try {
      setCameraLoading(true);
      setCameraError("");
      const camStream = await navigator.mediaDevices.getUserMedia({
        video: true,
      });
      if (videoRef.current) {
        videoRef.current.srcObject = camStream;
        await videoRef.current.play();
      }
      setStream(camStream); // << this will drive the UI
      setCameraLoading(false);
    } catch (err) {
      console.error("❌ Webcam permission denied:", err);
      setCameraError("Please allow camera access to continue");
      setStream(null); // no stream
      setCameraLoading(false);
    }
  };

  useEffect(() => {
    requestCameraAccess(); // try once on mount

    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  // Capture image using hidden video
  const captureImage = async () => {
    try {
      if (!videoRef.current || !stream) {
        console.warn("Camera not ready yet.");
        return null;
      }

      // Ensure video is rendering
      await new Promise((resolve) => {
        if (videoRef.current.readyState >= 2) {
          resolve();
        } else {
          videoRef.current.onloadeddata = resolve;
        }
      });

      const canvas = document.createElement("canvas");
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob);
        }, "image/png");
      });
    } catch (err) {
      if (err.name === "NotReadableError") {
        alert("Camera is already in use by another app or browser tab.");
      } else if (err.name === "NotAllowedError") {
        alert("Permission denied for camera.");
      } else if (err.name === "NotFoundError") {
        alert("No camera device found.");
      } else {
        console.error("Unexpected error:", err);
      }
      return null;
    }
  };

  const handleCheckIn = async (file) => {
    const nowString = new Date().toJSON();
    const checkInMoment = moment().tz(
      userData?.location_id === 1
        ? "Asia/Dubai"
        : userData?.location_id === 2
        ? "Asia/Kolkata"
        : "UTC"
    );
    const apiData = {
      user_id: userData?.id,
      photo: file,
      clock_in_time: nowString,
      date: moment(new Date()).format("YYYY-MM-DD"),
      location_id: userData?.location_id,
      location_data: JSON.stringify(address),
      status: getAttendanceStatus(checkInMoment, userData?.location_id),
    };

    console.log(apiData, nowString, checkInMoment);

    const formData = new FormData();
    Object.entries(apiData).forEach(([key, value]) => {
      formData.append(key, value);
    });

    await api
      .post("/api/attendance/clock-in", formData)
      .then((res) => {
        if (res) {
          // ✅ Start timer
          toast.success(res.data?.message, {
            position: "top-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
            transition: Bounce,
          });
          const resData = res.data?.data;
          localStorage.setItem("isCheckedIn", "true");
          localStorage.setItem("checkedInData", JSON.stringify(resData));
          setCheckInData(resData);
          const startTime = new Date(resData?.clock_in_time).getTime();
          setClockInTime(startTime);
          setIsCheckedIn(true);
          getAttendanceLog();
          setOpenCamera(false);
        }
      })
      .catch((err) => {
        toast.error(err.response.data.message, {
          position: "top-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
          transition: Bounce,
        });
        setOpenCamera(false);
      });
  };

  const handleCheckOut = async (file) => {
    const nowString = new Date().toJSON();
    const apiData = {
      user_id: userData?.id,
      photo: file,
      clock_out_time: nowString,
      date: moment(new Date()).format("YYYY-MM-DD"),
      // location_id: userData?.location_id,
    };

    const formData = new FormData();
    Object.entries(apiData).forEach(([key, value]) => {
      formData.append(key, value);
    });

    await api
      .post(`/api/attendance/clock-out/${checkInData?.id}`, formData)
      .then((res) => {
        if (res) {
          // ✅ Start timer
          toast.success(res.data?.message, {
            position: "top-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
            transition: Bounce,
          });
          setIsCheckedIn(false);
          setClockInTime(null);
          setTime(0); // also reset timer display on check out
          localStorage.setItem("isCheckedIn", "false");
          localStorage.removeItem("checkedInData");
          getAttendanceLog();
          setOpenCamera(false);
        }
      })
      .catch((err) => {
        toast.error(err.response.data.message, {
          position: "top-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
          transition: Bounce,
        });
        setOpenCamera(false);
      });
  };

  const handleCheckToggle = async () => {
    const photoBlob = await captureImage();
    if (!photoBlob) return alert("Camera not ready yet. Please try again.");

    const file = new File(
      [photoBlob],
      `checkin-${moment(new Date()).format("DD-MM-yyyy-hh-mm-ss")}.png`,
      { type: "image/png" }
    );
    if (!isCheckedIn) {
      if (file) {
        handleCheckIn(file);
      }
    } else {
      // ✅ Stop timer immediately
      if (file) {
        handleCheckOut(file);
      }
    }
  };

  useEffect(() => {
    if (!userData) return;

    const savedCheck = localStorage.getItem("isCheckedIn") === "true";
    const savedCheckInData = JSON.parse(localStorage.getItem("checkedInData"));

    if (
      savedCheck &&
      savedCheckInData &&
      savedCheckInData.user_id === userData.id
    ) {
      const today = moment().format("YYYY-MM-DD");
      const savedDate = moment(savedCheckInData.date).format("YYYY-MM-DD");

      if (savedDate === today) {
        // Valid same user, same day
        setCheckInData(savedCheckInData);
        setIsCheckedIn(true);

        const startTime = new Date(savedCheckInData.clock_in_time).getTime();
        // ✅ Only set clockInTime if it's not already set
        setClockInTime((prev) => (prev == null ? startTime : prev));
        return; // stop here so we don't reset below
      }
    }

    // Reset if different user/day or no check-in
    localStorage.removeItem("isCheckedIn");
    localStorage.removeItem("checkedInData");
    setIsCheckedIn(false);
    setCheckInData(null);
    setClockInTime(null);
  }, []);

  // one single effect to handle timer tick
  useEffect(() => {
    // clear any existing interval
    if (intervalRef.current) clearInterval(intervalRef.current);

    if (clockInTime) {
      intervalRef.current = setInterval(() => {
        setTime(Math.floor((Date.now() - clockInTime) / 1000));
      }, 1000);
    } else {
      // reset time if no clockInTime
      setTime(0);
    }

    // cleanup on unmount or when clockInTime changes
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [clockInTime]);

  const getAttendanceLog = async () => {
    await api
      .get(`/api/attendance/${userData?.id}`)
      .then((res) => {
        if (res && res.status === 200) {
          const today = moment().format("YYYY-MM-DD");

          // Find the log for today
          const todaysLog = res.data?.data?.find(
            (log) => moment(log.date).format("YYYY-MM-DD") === today
          );

          if (todaysLog && !todaysLog?.clock_out_time) {
            const startTime = new Date(todaysLog.clock_in_time).getTime();
            if (!isCheckedIn) {
              localStorage.setItem("isCheckedIn", "true");
              setIsCheckedIn(true);
            }
            if (!checkInData) {
              localStorage.setItem("checkedInData", JSON.stringify(todaysLog));
              setCheckInData(todaysLog);
            }
            setClockInTime(startTime);
          }
          let newAttendanceData = [];
          // res.data?.data.lengh > 0 &&
          res.data?.data.map((a) => {
            newAttendanceData.push({
              ...a,
              location_id: userData?.location_id,
            });
          });
          setAttendanceLog(newAttendanceData);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const getAllHolidays = async () => {
    api.get("/api/public-holidays").then((res) => {
      if (res && res.data) {
        setHolidayData(res.data);
      }
    });
  };

  useEffect(() => {
    getCurrentLocation();
    getAttendanceLog();
    getAllHolidays();
  }, []);

  const stats = [
    {
      title: "Today’s Status",
      value: isCheckedIn ? "Checked In" : "Not Checked In",
      Icon: UsersIcon,
    },
    { title: "Work Hours Today", value: formatTime(time), Icon: LogIcon },
    { title: "This Month", value: "22 Days", Icon: LeaveIcon },
    {
      title: "Current Location",
      value: address ? address.country : "N/A",
      Icon: DollarIcon,
    },
  ];

  const handleTimeSelect = (pic) => {
    setSelectedPhoto(pic);
    setOpenImage(true);
  };

  const handleClosePhoto = () => {
    setSelectedPhoto("");
    setOpenImage(false);
  };

  const handleCheckInCamera = () => {
    setCameraAction(isCheckedIn ? "Check Out" : "Check In");
    setOpenCamera(true);
  };

  useEffect(() => {
    if (openCamera && videoRef.current && stream) {
      videoRef.current.srcObject = stream;
      videoRef.current.play();
    }
  }, [openCamera, stream]);

  const handleCameraClose = () => {
    setOpenCamera(false);
    setCameraAction("");
  };

  return (
    <>
      {loading && <Loader opacity={90} />}
      <Popup
        isOpen={openCamera}
        onClose={handleCameraClose}
        title={`${cameraAction}`}
      >
        <div className="relative">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={`m-auto rounded-xl ${openCamera ? "" : "hidden"}`}
          />

          {/* Overlay while loading */}
          {cameraLoading && (
            <div className="flex flex-col h-80 items-center justify-center bg-black/60 text-white text-lg rounded-xl gap-4">
              Requesting camera permission…
            </div>
          )}

          {/* Overlay on error */}
          {cameraError && !cameraLoading && (
            <div className="bg-white p-6 max-w-sm w-full m-auto text-center h-auto">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Please allow camera access
              </h3>
              <p className="text-sm text-gray-500 mb-5">
                We need your camera to take check-in photos.
                <br />
                Click the button below to try again.
              </p>
              <button
                onClick={requestCameraAccess} // call your camera API here
                className="inline-flex items-center justify-center px-5 py-2 bg-[#c30f0a] hover:bg-[#814f27] text-white rounded-lg transition-colors"
              >
                Allow Camera
              </button>
              <p className="text-xs text-gray-400 mt-4">
                If it still doesn’t work, click the lock icon in your browser’s
                address bar and enable camera access manually.
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-4">
          <button
            onClick={handleCheckToggle}
            className="px-10 py-3.5 rounded-md bg-[#de2b26] text-white font-medium disabled:opacity-50"
          >
            {cameraAction}
          </button>
        </div>
      </Popup>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full mx-auto px-4 py-3 gap-4">
        <p>
          <span className="block text-xl sm:text-2xl font-medium text-[#de2b26]">
            Attendance
          </span>
          <span className="block text-sm text-[#142b70]/50">
            Track and manage your attendance
          </span>
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full mx-auto px-4 py-3">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      <div className="flex flex-col md:flex-row gap-6 w-full mx-auto px-4 py-3">
        {/* Right Side Box */}
        <WorkTimerCard
          time={formatTime(time)}
          isCheckedIn={isCheckedIn}
          handleClick={handleCheckInCamera}
        />

        {/* Calendar Card */}
        <Calendar events={holidayData} />
      </div>

      <div className="w-full mx-auto px-4 py-3 relative">
        <div className="w-full mx-auto px-4 py-3 relative rounded-xl bg-white border border-[#a48572]/10">
          <div className="flex flex-col justify-start items-start gap-[20px]">
            <div className="flex flex-col justify-start items-start flex-grow-0 flex-shrink-0 relative gap-5">
              <p className="flex-grow-0 flex-shrink-0 text-base font-medium text-left text-[#142b70]">
                Attendance History
              </p>
            </div>
            <AttendanceTable
              data={attendanceLog}
              handleSelectTime={handleTimeSelect}
            />
            <Popup
              isOpen={openImage}
              onClose={handleClosePhoto}
              title="Check In / Out Image"
            >
              <div className="flex flex-col md:flex-row items-start gap-6 mt-[30px]">
                <div className="flex flex-col gap-4 w-full md:w-1/2 mb-8">
                  <div className="text-[#c30f0a] text-lg">Check In Image</div>
                  <img
                    src={
                      selectedPhoto?.checkInImg
                        ? selectedPhoto?.checkInImg
                        : PlaceholderImage
                    }
                    alt="Check In Image"
                    className="w-full h-52 object-cover rounded-lg shadow-md"
                  />
                </div>
                <div className="flex flex-col gap-4 w-full md:w-1/2 mb-8">
                  <div className="text-[#c30f0a] text-lg">Check Out Image</div>
                  <img
                    src={
                      selectedPhoto?.checkOutImg
                        ? selectedPhoto?.checkOutImg
                        : PlaceholderImage
                    }
                    alt="Check Out Image"
                    className="w-full h-52 object-cover rounded-lg shadow-md"
                  />
                </div>
              </div>
            </Popup>
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default Attendance;
